import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { ProductUIStyles } from '../StepTwo/ProductUIStyles';
import {
  COLOR1,
  IS_POLITICALLY_EXPOSED_TOOLTIP_BUNDLE,
  TOOLTIP_POSITION_TOP_END,
} from '../../../constants';
import { isBundle } from '../../commonCode';
import CustomCheckedIcon from '../../Bundle/CheckBoxIcons/CustomCheckedIcon';
import CustomUncheckedIcon from '../../Bundle/CheckBoxIcons/CustomUncheckedIcon';

function Widget({ question, answerHandler, itemsAnswer, disable, isBundleCompliance = false }) {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { productName, theme, isLightTheme } = leadGenerationContext;
  const classes = ProductUIStyles(leadGenerationContext?.theme);
  const [translation, setTranslation] = useState(leadGenerationContext.productQuestionsEN);

  const [mainText, details] = question.questionDesc.split(' (');

  useEffect(() => {
    if (leadGenerationContext.language === 'EN') {
      setTranslation(leadGenerationContext.productQuestionsEN);
    }
    if (leadGenerationContext.language === 'ES') {
      setTranslation(leadGenerationContext.productQuestionsES);
    }
  }, [leadGenerationContext.language]);

  let trueValue = false;
  if (question.name in itemsAnswer) {
    trueValue = itemsAnswer[question.name];
  }
  const handleChange = (e) => {
    if (e.target.checked === true) {
      answerHandler(true, question);
    } else {
      answerHandler(false, question);
    }
  };

  const isPoliticallyExposedTooltip = `The term "politically exposed person" generally includes a current or former senior foreign political figure, their immediate family, and their close associates.`;
  const achTransactionTooltip =
    'Includes payroll deposits, pensions, social security benefits, etc., as well as direct payments such as auto loan and mortgage payments.';

  let message;
  const toolTipText = () => {
    if (question.name === 'IsPoliticallyExposed') {
      message = 'Politically Exposed Tooltip';
    } else if (question.name === 'ACHTransaction') {
      message = 'ACHTransaction Tooltip';
    } else {
      message = '';
    }
    return message;
  };

  let toolTipMsg;
  const toolTipMessage = () => {
    if (question.name === 'IsPoliticallyExposed') {
      toolTipMsg = isPoliticallyExposedTooltip;
    } else if (question.name === 'ACHTransaction') {
      toolTipMsg = achTransactionTooltip;
    } else {
      toolTipMsg = '';
    }
    return toolTipMsg;
  };

  const questionDesc = () => (question.questionDesc ? translation[question.questionDesc] : '');

  return (
    <div>
      {question?.widget?.type &&
      (isBundleCompliance ? isBundle(productName) : !isBundle(productName)) ? (
        <Grid container>
          <div
            data-testid="widget_testid"
            style={!isBundleCompliance ? { marginBottom: '1px' } : classes.widgetContainer}
          >
            <label className={isLightTheme ? 'checkbox_label' : 'checkbox_label_dark'}>
              <Box
                component="input"
                style={{ margin: '3px' }}
                type="checkbox"
                name={`${question.name}`}
                id={`${question.name}`}
                data-testid="checkbox_testid"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleChange(e);
                  }
                }}
                aria-label={translation[question.questionDesc]}
                autoFocus={
                  !!(
                    (question.name === 'IsPoliticallyExposed' && !isBundleCompliance) ||
                    question.name === 'CashTransaction'
                  )
                }
                disabled={disable}
                checked={trueValue}
                onChange={handleChange}
              />
              <span
                className={
                  isLightTheme ? 'checkbox_custom rectangular' : 'checkbox_custom_dark rectangular'
                }
                id={`${question.name}_span`}
              />
            </label>
            {(question.name === 'IsPoliticallyExposed' || question.name === 'ACHTransaction') &&
            question.questionDesc ? (
              <Box
                id={`${question.name}_label`}
                className={
                  !isBundleCompliance ? 'checkbox_label_style checkbox_content_position' : ''
                }
                sx={
                  isBundleCompliance
                    ? [
                        classes.widgetContainer,
                        { ...classes.widgetContainerFont, color: theme?.palette?.primary?.main },
                      ]
                    : classes.checkbox_content_adjust_for_info
                }
                style={
                  !isBundleCompliance
                    ? {
                        color: theme?.palette?.primary?.main,
                        font: 'normal 400 16px/18px Arial',
                        paddingRight: question.name === 'ACHTransaction' ? '25px' : '',
                      }
                    : null
                }
              >
                {translation[question.questionDesc]}
                <span
                  style={
                    isBundleCompliance
                      ? { padding: '2px' }
                      : { marginLeft: '10px', top: '1px', position: 'relative' }
                  }
                >
                  <CustomTooltip
                    text={
                      isBundleCompliance
                        ? t(IS_POLITICALLY_EXPOSED_TOOLTIP_BUNDLE, {
                            defaultValue: IS_POLITICALLY_EXPOSED_TOOLTIP_BUNDLE,
                          })
                        : t(toolTipText(), {
                            defaultValue: toolTipMessage(),
                          })
                    }
                    placement={isBundleCompliance && TOOLTIP_POSITION_TOP_END}
                    isBundleCompliance={isBundleCompliance}
                  />
                </span>
              </Box>
            ) : (
              <span
                id={`${question.name}_label`}
                className="checkbox_label_style checkbox_content_position"
                style={{
                  top: '-20px',
                  marginRight: '30px',
                  position: 'relative',
                  color: theme?.palette?.primary?.main,
                  font: 'normal 400 16px/18px Arial',
                }}
              >
                {' '}
                {questionDesc()}
              </span>
            )}
          </div>
        </Grid>
      ) : (
        <Grid container>
          <div data-testid="widget_testid" style={{ marginBottom: '1px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  disableFocusRipple
                  disableTouchRipple
                  icon={<CustomUncheckedIcon color={theme?.palette?.orientalPrimary?.main} />}
                  checkedIcon={<CustomCheckedIcon color={theme?.palette?.orientalPrimary?.main} />}
                  style={{ margin: '3px' }}
                  sx={{
                    '&.Mui-focusVisible': {
                      border: `1px solid ${theme?.palette?.orientalPrimary?.main}`,
                      borderRadius: '5px',
                      padding: '3px',
                      margin: '10px !important',
                    },
                  }}
                  type="checkbox"
                  name={question.name}
                  id={`${question.name}`}
                  data-testid="checkbox_testid"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleChange(e);
                    }
                  }}
                  aria-label={translation[question.questionDesc]}
                  autoFocus={
                    !!(
                      question.name === 'IsPoliticallyExposed' ||
                      question.name === 'CashTransaction'
                    )
                  }
                  disabled={disable}
                  checked={trueValue}
                  onChange={handleChange}
                />
              }
              label={
                (question.name === 'IsPoliticallyExposed' || question.name === 'ACHTransaction') &&
                question.questionDesc ? (
                  <Box
                    component="div"
                    id={`${question.name}_label`}
                    // className="checkbox_label_style checkbox_content_position"
                  >
                    <Typography component="p" variant="subtitle2">
                      {t(mainText, {
                        defaultValue: mainText,
                      })}
                    </Typography>
                    <Box component="span" sx={{ display: 'flex' }}>
                      {details && (
                        <Typography component="p" sx={{ fontSize: '11px', color: '#808285' }}>
                          {t(`(${details}`, {
                            defaultValue: `(${details}`,
                            nsSeparator: '|',
                          })}
                        </Typography>
                      )}
                      {/* &ensp;
                      <CustomTooltip
                        text={t(toolTipText(), {
                          defaultValue: toolTipMessage(),
                        })}
                      /> */}
                    </Box>
                    {/* {translation[question.questionDesc]} */}
                  </Box>
                ) : (
                  // <span
                  //   id={`${question.name}_label`}
                  //   className="checkbox_label_style checkbox_content_position"
                  //   style={{
                  //     top: '-20px',
                  //     marginRight: '30px',
                  //     position: 'relative',
                  //     color: '#000000',
                  //     font: 'normal 400 16px/18px Arial',
                  //   }}
                  // >
                  // </span>
                  <Typography
                    component="p"
                    variant="subtitle2"
                    id={`${question.name}_label`}
                    // className="checkbox_label_style checkbox_content_position"
                  >
                    {questionDesc()}
                  </Typography>
                )
              }
            />
          </div>
        </Grid>
      )}
    </div>
  );
}

Widget.propTypes = {
  question: PropTypes.object,
  answerHandler: PropTypes.func,
  itemsAnswer: PropTypes.object,
  disable: PropTypes.bool,
  isBundleCompliance: PropTypes.bool,
};
export default Widget;
